/**
 * Google Analytics 4 (gtag.js) Plugin
 * 
 * Initializes Google Analytics 4 tracking on client-side.
 * Measurement ID configured via NUXT_PUBLIC_GA_MEASUREMENT_ID environment variable.
 * 
 * Spike Task: 112-141-T000-B - Event Tracking Integration
 * This plugin enables CTA click tracking and other analytics events.
 */

import { defineNuxtPlugin, useRuntimeConfig } from 'nuxt/app';
import {
  emitAnalyticsOperationalSignal,
  getAnalyticsConsentBlockedReason,
  hasAnalyticsConsent,
  subscribeAnalyticsConsentChanges,
} from '../../composables/useAnalyticsConsent';

declare global {
  function gtag(...args: unknown[]): void;
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export default defineNuxtPlugin((_nuxtApp) => {
  const config = useRuntimeConfig();
  const measurementId = typeof config.public.gaMeasurementId === 'string'
    ? config.public.gaMeasurementId
    : '';
  let isInitialized = false;
  let isInitializing = false;
  let telemetryEnabled = false;
  let consentRevoked = false;
  let lastBlockedReason: string | null = null;

  function scheduleInitialization(): void {
    if (typeof globalThis.window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        void initializeGa4();
      }, { timeout: 2000 });
      return;
    }

    void initializeGa4();
  }

  function emitBlocked(reason: string): void {
    if (lastBlockedReason === reason) return;
    lastBlockedReason = reason;
    emitAnalyticsOperationalSignal({
      code: 'analytics_blocked',
      channel: 'ga4',
      reason,
    });
  }

  function loadGtagScript(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>(
        `script[src="https://www.googletagmanager.com/gtag/js?id=${id}"]`
      );

      if (existingScript) {
        if (existingScript.dataset.loaded === 'true') {
          resolve();
          return;
        }
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener('error', () => reject(new Error('script_load_error')), { once: true });
        return;
      }

      const script = document.createElement('script');
      const timeoutId = window.setTimeout(() => {
        script.remove();
        reject(new Error('script_load_timeout'));
      }, 8000);

      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
      script.addEventListener('load', () => {
        window.clearTimeout(timeoutId);
        script.dataset.loaded = 'true';
        resolve();
      }, { once: true });
      script.addEventListener('error', () => {
        window.clearTimeout(timeoutId);
        reject(new Error('script_load_error'));
      }, { once: true });

      document.head.appendChild(script);
    });
  }

  async function initializeGa4(): Promise<void> {
    if (isInitialized || isInitializing) return;

    if (!measurementId) {
      emitBlocked('missing_measurement_id');
      return;
    }

    if (consentRevoked) {
      emitBlocked('consent_revoked');
      return;
    }

    if (!hasAnalyticsConsent()) {
      emitBlocked(getAnalyticsConsentBlockedReason());
      return;
    }

    isInitializing = true;

    try {
      await loadGtagScript(measurementId);

      window.dataLayer = window.dataLayer || [];
      if (typeof window.gtag !== 'function') {
        window.gtag = (...args: unknown[]) => {
          (window.dataLayer as unknown[]).push(args);
        };
      }

      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        send_page_view: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });

      isInitialized = true;
      telemetryEnabled = true;
      lastBlockedReason = null;
      emitAnalyticsOperationalSignal({
        code: 'analytics_initialized',
        channel: 'ga4',
      });
    } catch (error) {
      telemetryEnabled = false;
      const reason = error instanceof Error ? error.message : 'unknown_error';
      emitAnalyticsOperationalSignal({
        code: 'analytics_init_failed',
        channel: 'ga4',
        reason,
      });
    } finally {
      isInitializing = false;
    }
  }

  if (hasAnalyticsConsent()) {
    scheduleInitialization();
  } else {
    emitBlocked(getAnalyticsConsentBlockedReason());
  }

  const unsubscribe = subscribeAnalyticsConsentChanges((granted) => {
    if (granted) {
      emitAnalyticsOperationalSignal({
        code: 'consent_granted',
        channel: 'ga4',
      });
      scheduleInitialization();
      return;
    }

    consentRevoked = true;
    telemetryEnabled = false;
    emitAnalyticsOperationalSignal({
      code: 'consent_denied',
      channel: 'ga4',
      reason: 'consent_revoked',
    });
    emitBlocked('consent_revoked');
  });

  // Provide gtag function for composables/components
  return {
    provide: {
      gtag: (action: string, category: string, label?: string, value?: number) => {
        if (!telemetryEnabled || typeof window.gtag !== 'function') return;
        window.gtag('event', action, {
          event_category: category,
          event_label: label,
          value,
        });
      },
    },
  };
});
